import React from 'react';
import { SUBTLE, MUTED, HAIRLINE, INSET } from './tokens';
import { useSoft, SOFT } from './SurfaceStyleContext';

/**
 * The 2px-bordered white card every section lives in. `black` flips it to the black
 * panels (door mode, MIME round, THIS CLUB needs-you). Internal dividers are hairlines.
 */
export function Panel({ children, black = false, className = '', style = {}, as: Tag = 'section', bg, soft: softProp, dense = false, ...rest }) {
  const soft = useSoft(softProp);
  if (soft) {
    // Soft: a borderless white card, 26px padding (18px dense / phones), children stacked 14px apart.
    return (
      <Tag
        className={`flex flex-col ${black ? 'bg-black text-white' : 'bg-white text-black'} ${className}`}
        style={{ padding: dense ? '18px 16px' : 26, gap: 14, ...(bg ? { background: bg } : {}), ...style }}
        data-soft="1"
        {...rest}
      >
        {children}
      </Tag>
    );
  }
  return (
    <Tag
      className={`border-2 border-black ${black ? 'bg-black text-white' : 'bg-white text-black'} ${className}`}
      style={bg ? { background: bg, ...style } : style}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** Band label header: 11px/700/0.1em left, optional 9px subtle note or action controls right. */
export function PanelHeader({ label, right, children, black = false, className = '', dense = false }) {
  const soft = useSoft();
  if (soft) {
    // Soft: 12px/700/.16em label, 9px faint note on the right, no rule.
    return (
      <div className={`flex flex-wrap items-center justify-between gap-2 ${className}`}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: dense ? 11 : 12, letterSpacing: '0.16em' }}>{label}</span>
        {right !== undefined && right !== null && (
          typeof right === 'string'
            ? <span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.14em', color: SOFT.faint }}>{right}</span>
            : <div className="flex items-center gap-2">{right}</div>
        )}
        {children}
      </div>
    );
  }
  return (
    <div
      className={`flex items-center justify-between gap-3 border-b-2 ${black ? 'border-white' : 'border-black'} ${className}`}
      style={{ padding: dense ? '9px 12px' : '10px 16px' }}
    >
      <span className="font-mono font-bold uppercase" style={{ fontSize: dense ? 10 : 11, letterSpacing: '0.1em' }}>{label}</span>
      {right !== undefined && right !== null && (
        typeof right === 'string'
          ? <span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: SUBTLE }}>{right}</span>
          : <div className="flex items-center gap-2">{right}</div>
      )}
      {children}
    </div>
  );
}

/** Title + subtitle header used by composer-style panels (POST AN UPDATE, START ANOTHER ROUND). */
export function PanelTitle({ title, sub, right, black = false }) {
  const soft = useSoft();
  return (
    <div className={`flex items-center justify-between gap-3 ${soft ? '' : `border-b-2 ${black ? 'border-white' : 'border-black'}`}`} style={{ padding: soft ? 0 : '12px 16px' }}>
      <span>
        <span className="block font-mono font-bold uppercase" style={{ fontSize: 13, letterSpacing: '0.05em' }}>{title}</span>
        {sub && <span className="block font-mono" style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>{sub}</span>}
      </span>
      {right}
    </div>
  );
}

export function PanelBody({ children, className = '', style = {}, dense = false }) {
  const soft = useSoft();
  return <div className={`flex flex-col ${className}`} style={{ padding: soft ? 0 : dense ? 12 : 16, gap: 12, ...style }}>{children}</div>;
}

/** Full-width footer action, e.g. `VIEW ALL 28 GUESTS`, `+ OFFER SOMETHING`. */
export function PanelFooterButton({ children, onClick, className = '', ...rest }) {
  const soft = useSoft();
  if (soft) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full rounded-full font-mono font-bold uppercase text-black transition-colors duration-200 hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        style={{ background: SOFT.chip, height: 48, padding: '0 18px', fontSize: 11, letterSpacing: '0.14em' }}
        {...rest}
      >
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full border-t-2 border-black bg-white font-mono font-bold uppercase text-black transition-colors duration-200 hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{ padding: 13, fontSize: 11, letterSpacing: '0.1em', minHeight: 46 }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Footer note with a bordered tag: `ESCROW  Your money sits with…` */
export function PanelNote({ tag, children, black = false }) {
  const soft = useSoft();
  if (soft) {
    // Soft: the #F5F5F5 18px-radius note with a black mini-pill tag (the rail's ESCROW note).
    return (
      <div className="flex items-start gap-[10px]" style={{ padding: '12px 14px', borderRadius: 18, background: black ? '#1A1A1A' : SOFT.canvas }}>
        {tag && (
          <span className="flex-shrink-0 rounded-full font-mono font-bold uppercase" style={{ fontSize: 8, letterSpacing: '0.14em', padding: '3px 8px', background: black ? '#fff' : '#000', color: black ? '#000' : '#fff' }}>{tag}</span>
        )}
        <span className="font-mono" style={{ fontSize: 11, color: black ? SUBTLE : SOFT.body, lineHeight: 1.55 }}>{children}</span>
      </div>
    );
  }
  return (
    <div className={`flex items-start gap-[10px] border-t-2 ${black ? 'border-white' : 'border-black'}`} style={{ padding: '12px 16px' }}>
      {tag && (
        <span className={`flex-shrink-0 border-2 ${black ? 'border-white' : 'border-black'} font-mono font-bold uppercase`} style={{ fontSize: 9, letterSpacing: '0.1em', padding: '3px 6px' }}>{tag}</span>
      )}
      <span className="font-mono" style={{ fontSize: 11, color: black ? SUBTLE : MUTED, lineHeight: 1.55 }}>{children}</span>
    </div>
  );
}

/** A row inside a panel; hairline between rows, no line after the last. */
export function PanelRow({ children, last = false, className = '', style = {}, dense = false, inset = false, ...rest }) {
  const soft = useSoft();
  return (
    <div
      className={`flex items-center gap-3 ${className}`}
      style={soft
        ? { padding: dense ? '9px 0' : '11px 0', borderBottom: last ? 'none' : `2px solid ${SOFT.chip}`, ...style }
        : { padding: dense ? '9px 12px' : '11px 16px', borderBottom: last ? 'none' : `2px solid ${HAIRLINE}`, background: inset ? INSET : undefined, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Key/value line for money blocks: muted key left, bold value right. */
export function KVLine({ k, v, bold = true, color, size = 12, className = '', style = {} }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 font-mono ${className}`} style={{ fontSize: size, ...style }}>
      <span style={{ color: MUTED }}>{k}</span>
      <span style={{ fontWeight: bold ? 700 : 400, color }}>{v}</span>
    </div>
  );
}

export default Panel;
